
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { MaintenanceTicket, Project, ProjectTask, ProjectPhase, StandardOperatingProcedure } from '@repo/shared-types';
import { useToast } from '../../components/common/ToastContext';
import { projectPhaseSchema, projectTaskSchema, type ProjectPhase as ProjectPhaseType } from '@repo/shared-types/projects/schemas';
import { maintenanceTicketSchema, projectSchema, sopSchema, sopInstanceSchema } from '@repo/shared-types/maintenance/schemas';
import { entityNoteListItemSchema } from '@repo/shared-types/common/noteSchemas';
import type { Note } from '@repo/shared-types/common/notes';
import { nullToUndefined } from '@repo/shared-utils';
import { validateEntity, validateEntityArray } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';
import { getFriendlyErrorMessage } from '@repo/shared-utils';

// --- TICKETS ---

/**
 * React Query hook to fetch maintenance tickets.
 * @param statusFilterOrFilters - Optional status filter string ('All', 'Open', 'Completed', etc.) or filters object (status, propertyId, unitId, assignedVendorId)
 * @returns Query result containing filtered tickets array
 */
export const useTickets = (statusFilterOrFilters?: string | Record<string, string>) => {
  return useQuery({
    queryKey: ['tickets', statusFilterOrFilters ?? 'All'],
    queryFn: async () => {
      const raw = await api.maintenance.tickets.list(statusFilterOrFilters);
      const list = Array.isArray(raw) ? raw : (raw as { data: MaintenanceTicket[] }).data;
      const validation = validateEntityArray(maintenanceTicketSchema, list);
      if (!validation.valid) {
        logger.warn('Tickets query response did not match schema; returning empty list', { errors: validation.errors });
        return [] as MaintenanceTicket[];
      }
      return validation.data! as MaintenanceTicket[];
    },
    staleTime: 1000 * 60,
  });
};

export const useTicketsPaginated = (filters: Record<string, string>) => {
  return useQuery({
    queryKey: ['tickets', 'paginated', filters],
    queryFn: async () => {
      const raw = await api.maintenance.tickets.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: MaintenanceTicket[] }).data;
      const total = Array.isArray(raw) ? raw.length : (raw as { data: MaintenanceTicket[]; total: number }).total;
      const validation = validateEntityArray(maintenanceTicketSchema, list);
      if (!validation.valid) {
        logger.warn('Tickets paginated query response did not match schema; returning empty list', { errors: validation.errors });
        return { data: [] as MaintenanceTicket[], total: 0 };
      }
      return { data: validation.data! as MaintenanceTicket[], total };
    },
    staleTime: 1000 * 60,
  });
};

/**
 * React Query hook to fetch a single maintenance ticket by ID.
 * @param ticketId - The ID of the ticket to fetch, or null to skip the query
 * @returns Query result containing the ticket or undefined
 */
export const useTicket = (ticketId: string | null) => {
  return useQuery({
    queryKey: ['tickets', ticketId],
    queryFn: async () => {
      const data = await api.maintenance.tickets.get(ticketId!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(maintenanceTicketSchema, data);
        if (!validation.valid) {
          logger.error('Invalid ticket query response:', validation.errors);
          throw new Error('Invalid ticket data received from API');
        }
        return validation.data! as MaintenanceTicket;
      }
      return null;
    },
    enabled: !!ticketId, // Only fetch if ID exists
  });
};

/**
 * React Query mutation hook to create a new maintenance ticket.
 * Automatically invalidates tickets query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useCreateTicket = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (newTicket: Omit<MaintenanceTicket, 'id' | 'createdAt'>) => api.maintenance.tickets.create(newTicket),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(maintenanceTicketSchema, data);
      if (!validation.valid) {
        logger.error('Invalid ticket response from API:', validation.errors);
        showToast('Work Order created but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['tickets'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      showToast('Work Order created successfully', 'success');
    },
    onError: () => {
      showToast('Failed to create ticket', 'error');
    }
  });
};

/**
 * React Query mutation hook to update an existing maintenance ticket.
 * Automatically invalidates tickets query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useUpdateTicket = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<MaintenanceTicket> }) =>
      api.maintenance.tickets.update(id, updates),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(maintenanceTicketSchema, data);
      if (!validation.valid) {
        logger.error('Invalid ticket response from API:', validation.errors);
        showToast('Ticket updated but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['tickets'] });
        return;
      }
      const validatedTicket: MaintenanceTicket = nullToUndefined(validation.data!) as MaintenanceTicket;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tickets'] }),
        queryClient.invalidateQueries({ queryKey: ['tickets', validatedTicket.id] }),
      ]);
      showToast('Ticket updated', 'success');
    }
  });
};

export const useDeleteTicket = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.maintenance.tickets.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      showToast('Ticket deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete ticket', 'error');
    }
  });
};

/** Fetch available time slots for a ticket (when vendor is assigned). */
export const useTicketAvailableSlots = (ticketId: string | null, params?: { from?: string; to?: string }) => {
  return useQuery({
    queryKey: ['tickets', ticketId, 'available-slots', params?.from, params?.to],
    queryFn: () => api.maintenance.tickets.getAvailableSlots(ticketId!, params),
    enabled: !!ticketId,
  });
};

/** Schedule a ticket at a specific slot. */
export const useScheduleTicket = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ ticketId, slotStart }: { ticketId: string; slotStart: string }) =>
      api.maintenance.tickets.schedule(ticketId, { slotStart }),
    onSuccess: async (_data, { ticketId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tickets'] }),
        queryClient.invalidateQueries({ queryKey: ['tickets', ticketId] }),
      ]);
      showToast('Work order scheduled', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to schedule', 'error');
    },
  });
};

// --- VENDOR AVAILABILITY ---

export const useVendorAvailabilityWindows = (vendorId: string | null) => {
  return useQuery({
    queryKey: ['vendors', vendorId, 'availability'],
    queryFn: () => api.maintenance.vendors.availability.list(vendorId!),
    enabled: !!vendorId,
  });
};

export const useCreateVendorAvailabilityWindow = (vendorId: string | null) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (window: { dayOfWeek: number; startTime: string; endTime: string; effectiveFrom?: string; effectiveTo?: string }) =>
      api.maintenance.vendors.availability.create(vendorId!, window),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vendors', vendorId, 'availability'] });
      showToast('Availability window added', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to add availability', 'error');
    },
  });
};

export const useUpdateVendorAvailabilityWindow = (vendorId: string | null) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ windowId, updates }: { windowId: string; updates: Partial<{ dayOfWeek: number; startTime: string; endTime: string; effectiveFrom?: string; effectiveTo?: string }> }) =>
      api.maintenance.vendors.availability.update(windowId, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vendors', vendorId, 'availability'] });
      showToast('Availability window updated', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update availability', 'error');
    },
  });
};

export const useDeleteVendorAvailabilityWindow = (vendorId: string | null) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (windowId: string) => api.maintenance.vendors.availability.delete(windowId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vendors', vendorId, 'availability'] });
      showToast('Availability window removed', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to remove availability', 'error');
    },
  });
};

// --- PROJECTS ---

/**
 * React Query hook to fetch all projects.
 * @returns Query result containing projects array
 */
export const useProjects = () => {
  return useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const data = await api.projects.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(projectSchema, data);
      if (!validation.valid) {
        logger.error('Invalid projects query response:', validation.errors);
        throw new Error('Invalid projects data received from API');
      }
      return validation.data! as Project[];
    },
  });
};

/**
 * React Query hook to fetch a single project by ID.
 * @param projectId - The ID of the project to fetch
 * @returns Query result containing the project or undefined
 */
export const useProject = (projectId: string) => {
  return useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => api.projects.get(projectId),
    enabled: !!projectId
  });
};

/**
 * React Query hook to fetch tasks for a specific project.
 * @param projectId - The ID of the project to fetch tasks for
 * @returns Query result containing project tasks array
 */
export const useProjectTasks = (projectId: string) => {
  return useQuery({
    queryKey: ['projects', projectId, 'tasks'],
    queryFn: () => api.projects.tasks.list(projectId),
    enabled: !!projectId
  });
};

/**
 * React Query mutation hook to create a new project.
 * Automatically invalidates projects query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (project: Project) => api.projects.create(project),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(projectSchema, data);
      if (!validation.valid) {
        logger.error('Invalid project response from API:', validation.errors);
        showToast('Project created but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['projects'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      showToast('Project created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create project', 'error');
    }
  });
};

/**
 * React Query mutation hook to update a project.
 * Automatically invalidates projects query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Project> }) =>
      api.projects.update(id, updates),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(projectSchema, data);
      if (!validation.valid) {
        logger.error('Invalid project response from API:', validation.errors);
        showToast('Project updated but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['projects'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      showToast('Project updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update project', 'error');
    }
  });
};

/**
 * React Query mutation hook to delete a project.
 * Automatically invalidates projects query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.projects.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      showToast('Project deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete project', 'error');
    }
  });
};

/**
 * React Query mutation hook to create a project task.
 * Automatically invalidates project tasks query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useCreateProjectTask = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ projectId, taskData }: { projectId: string; taskData: Omit<ProjectTask, 'id' | 'projectId'> }) =>
      api.projects.tasks.create(projectId, taskData),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(projectTaskSchema, data);
      if (!validation.valid) {
        logger.error('Invalid project task response from API:', validation.errors);
        showToast('Task created but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['projects'] });
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
      ]);
      showToast('Task created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create task', 'error');
    }
  });
};

/**
 * React Query mutation hook to update a project task.
 * Automatically invalidates project tasks query cache.
 * @returns Mutation object with mutate function
 */
export const useUpdateProjectTask = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ projectId, id, updates }: { projectId: string; id: string; updates: Partial<ProjectTask> }) => api.projects.tasks.update(projectId, id, updates),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(projectTaskSchema, data);
      if (!validation.valid) {
        logger.error('Invalid project task response from API:', validation.errors);
        showToast('Task updated but received invalid data from server', 'error');
        if (data?.projectId) {
          await queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'tasks'] });
        }
        return;
      }
      if (data.projectId) {
        await queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'tasks'] });
      }
      showToast('Task updated successfully', 'success');
    }
  });
};

// --- PROJECT PHASES ---

/**
 * React Query hook to fetch project phases.
 * @param projectId - The ID of the project
 * @returns Query result containing phases array
 */
export const useProjectPhases = (projectId: string) => {
  return useQuery({
    queryKey: ['projects', projectId, 'phases'],
    queryFn: () => api.projects.phases.list(projectId),
    enabled: !!projectId,
  });
};

/**
 * React Query mutation hook to create a project phase.
 * @returns Mutation object with mutate function
 */
export const useCreateProjectPhase = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ projectId, phaseData }: { projectId: string; phaseData: Omit<ProjectPhase, 'id' | 'projectId' | 'images'> }) =>
      api.projects.phases.create(projectId, phaseData),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'phases'] }),
        queryClient.invalidateQueries({ queryKey: ['projects', data.projectId] }),
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
      ]);
      showToast('Phase created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create phase', 'error');
    }
  });
};

/**
 * React Query mutation hook to update a project phase.
 * @returns Mutation object with mutate function
 */
export const useUpdateProjectPhase = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ projectId, id, updates }: { projectId: string; id: string; updates: Partial<ProjectPhase> }) =>
      api.projects.phases.update(projectId, id, updates),
    onSuccess: async (data) => {
      // Validate API response with Zod schema
      const validation = validateEntity(projectPhaseSchema, data);
      if (!validation.valid) {
        logger.error('Invalid phase data from API:', validation.errors);
        showToast('Phase updated but received invalid data from server', 'error');
        // Still invalidate queries even if validation fails
        await queryClient.invalidateQueries({ queryKey: ['projects'] });
        return;
      }

      // Get projectId from validated phase
      const phase: ProjectPhaseType = validation.data!;
      const invalidations: Promise<unknown>[] = [queryClient.invalidateQueries({ queryKey: ['projects'] })];
      if (phase.projectId) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ['projects', phase.projectId, 'phases'] }),
          queryClient.invalidateQueries({ queryKey: ['projects', phase.projectId] }),
        );
      }
      await Promise.all(invalidations);
      showToast('Phase updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update phase', 'error');
    }
  });
};

/**
 * React Query mutation hook to approve a project phase.
 * @returns Mutation object with mutate function
 */
export const useApproveProjectPhase = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ projectId, id, notes }: { projectId: string; id: string; notes?: string }) =>
      api.projects.phases.approve(projectId, id, notes),
    onSuccess: async (data) => {
      // Validate API response with Zod schema
      const validation = validateEntity(projectPhaseSchema, data);
      if (!validation.valid) {
        logger.error('Invalid phase response from API:', validation.errors);
        showToast('Phase approved but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['projects'] });
        return;
      }

      // Get projectId from validated phase
      const phase: ProjectPhaseType = validation.data!;
      const invalidations: Promise<unknown>[] = [queryClient.invalidateQueries({ queryKey: ['projects'] })];
      if (phase.projectId) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ['projects', phase.projectId, 'phases'] }),
          queryClient.invalidateQueries({ queryKey: ['projects', phase.projectId] }),
        );
      }
      await Promise.all(invalidations);
      showToast('Phase approved successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to approve phase', 'error');
    }
  });
};

/**
 * React Query mutation hook to reject a project phase.
 * @returns Mutation object with mutate function
 */
export const useRejectProjectPhase = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ projectId, id, notes }: { projectId: string; id: string; notes: string }) =>
      api.projects.phases.reject(projectId, id, notes),
    onSuccess: async (data) => {
      // Validate API response with Zod schema
      const validation = validateEntity(projectPhaseSchema, data);
      if (!validation.valid) {
        logger.error('Invalid phase response from API:', validation.errors);
        showToast('Phase rejected but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['projects'] });
        return;
      }

      // Get projectId from validated phase
      const phase: ProjectPhaseType = validation.data!;
      const invalidations: Promise<unknown>[] = [queryClient.invalidateQueries({ queryKey: ['projects'] })];
      if (phase.projectId) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ['projects', phase.projectId, 'phases'] }),
          queryClient.invalidateQueries({ queryKey: ['projects', phase.projectId] }),
        );
      }
      await Promise.all(invalidations);
      showToast('Phase rejected - improvements requested', 'error');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to reject phase', 'error');
    }
  });
};

// --- SOPs ---

/**
 * React Query hook to fetch all standard operating procedures (SOPs).
 * @returns Query result containing SOPs array
 */
export const useSOPs = () => {
  return useQuery({
    queryKey: ['maintenance', 'sops'],
    queryFn: async () => {
      const data = await api.maintenance.sops.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(sopSchema, data);
      if (!validation.valid) {
        logger.error('Invalid SOPs query response:', validation.errors);
        throw new Error('Invalid SOPs data received from API');
      }
      return validation.data! as StandardOperatingProcedure[];
    },
  });
};

/**
 * React Query mutation hook to create a new standard operating procedure.
 * Automatically invalidates SOPs query cache.
 * @returns Mutation object with mutate function
 */
export const useCreateSOP = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (sop: StandardOperatingProcedure) => api.maintenance.sops.create(sop),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(sopSchema, data);
      if (!validation.valid) {
        logger.error('Invalid SOP response from API:', validation.errors);
        showToast('SOP created but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['maintenance', 'sops'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['maintenance', 'sops'] });
      showToast('SOP created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create SOP', 'error');
    }
  });
};

export const useUpdateSOP = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<StandardOperatingProcedure> }) =>
      api.maintenance.sops.update(id, updates),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(sopSchema, data);
      if (!validation.valid) {
        logger.error('Invalid SOP response from API:', validation.errors);
        showToast('SOP updated but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['maintenance', 'sops'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['maintenance', 'sops'] });
      showToast('SOP updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update SOP', 'error');
    }
  });
};

export const useDeleteSOP = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (id: string) => api.maintenance.sops.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance', 'sops'] });
      showToast('SOP deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete SOP', 'error');
    }
  });
};

// --- SOP INSTANCES ---

/**
 * React Query hook to fetch SOP instances.
 * @param filters - Optional filters for workOrderId, projectId, projectPhaseId, unitTurnId, inspectionId
 * @returns Query result containing SOP instances array
 */
export const useSOPInstances = (filters?: { workOrderId?: string; projectId?: string; projectPhaseId?: string; unitTurnId?: string; inspectionId?: string }) => {
  return useQuery({
    queryKey: ['sopInstances', filters],
    queryFn: async () => {
      const data = await api.maintenance.sops.instances.list(filters);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(sopInstanceSchema, data);
      if (!validation.valid) {
        logger.error('Invalid SOP instances query response:', validation.errors);
        throw new Error('Invalid SOP instances data received from API');
      }
      return validation.data! as import('types').SOPInstance[];
    },
    staleTime: 1000 * 60, // Data is fresh for 1 minute
  });
};

/**
 * React Query hook to fetch a single SOP instance by ID.
 * @param instanceId - The ID of the instance to fetch, or null to skip the query
 * @returns Query result containing the instance or undefined
 */
export const useSOPInstance = (instanceId: string | null) => {
  return useQuery({
    queryKey: ['sopInstances', instanceId],
    queryFn: async () => {
      const data = await api.maintenance.sops.instances.get(instanceId!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(sopInstanceSchema, data);
        if (!validation.valid) {
          logger.error('Invalid SOP instance query response:', validation.errors);
          throw new Error('Invalid SOP instance data received from API');
        }
        return validation.data! as import('types').SOPInstance;
      }
      return null;
    },
    enabled: !!instanceId,
  });
};

/**
 * React Query mutation hook to create a new SOP instance.
 * @returns Mutation object with mutate function
 */
export const useCreateSOPInstance = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (instanceData: Omit<import('types').SOPInstance, 'id' | 'completionPercentage'>) =>
      api.maintenance.sops.instances.create(instanceData),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sopInstances'] }),
        queryClient.invalidateQueries({ queryKey: ['sops'] }), // Update SOP usage stats
      ]);
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create SOP instance', 'error');
    }
  });
};

/**
 * React Query mutation hook to update an SOP instance.
 * @returns Mutation object with mutate function
 */
export const useUpdateSOPInstance = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<import('types').SOPInstance> }) =>
      api.maintenance.sops.instances.update(id, updates),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sopInstances'] }),
        queryClient.invalidateQueries({ queryKey: ['sops'] }), // Update SOP usage stats
      ]);
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update SOP instance', 'error');
    }
  });
};

// TICKET ATTACHMENTS
export const useTicketAttachments = (ticketId: string | undefined) => {
  return useQuery({
    queryKey: ['tickets', ticketId, 'attachments'],
    queryFn: () => api.maintenance.tickets.attachments.list(ticketId!),
    enabled: !!ticketId,
  });
};

export const useAddTicketAttachment = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ ticketId, file, description }: { ticketId: string; file: File; description?: string }) =>
      api.maintenance.tickets.attachments.add(ticketId, file, description),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['tickets', data.entityId, 'attachments'] });
      showToast('File attached successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to attach file', 'error');
    }
  });
};

export const useDeleteTicketAttachment = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (fileId: string) => api.maintenance.tickets.attachments.delete(fileId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      showToast('File deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete file', 'error');
    }
  });
};

/**
 * React Query mutation hook to approve a work order (Owner only).
 * @returns Mutation object with mutate function
 */
export const useApproveWorkOrder = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      api.maintenance.tickets.approve(id, notes),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tickets'] }),
        queryClient.invalidateQueries({ queryKey: ['tickets', data.id] }),
      ]);
      showToast('Work order approved successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to approve work order', 'error');
    }
  });
};

/**
 * React Query mutation hook to reject a work order (Owner only).
 * @returns Mutation object with mutate function
 */
export const useRejectWorkOrder = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      api.maintenance.tickets.reject(id, notes),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tickets'] }),
        queryClient.invalidateQueries({ queryKey: ['tickets', data.id] }),
      ]);
      showToast('Work order rejected', 'error');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to reject work order', 'error');
    }
  });
};

/** PM/Admin: audited release from Pending Approval into active work. */
export const usePmReleasePendingWorkOrder = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      reason,
      nextStatus,
    }: {
      id: string;
      reason: string;
      nextStatus?: 'In Progress' | 'Open';
    }) => api.maintenance.tickets.pmReleasePendingApproval(id, { reason, nextStatus: nextStatus ?? 'In Progress' }),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tickets'] }),
        queryClient.invalidateQueries({ queryKey: ['tickets', data.id] }),
      ]);
      showToast('Work order released from pending approval', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to release work order', 'error');
    },
  });
};

// --- WORK ORDER NOTES ---

export const useWorkOrderNotes = (workOrderId: string) => {
  return useQuery({
    queryKey: ['tickets', 'notes', workOrderId],
    queryFn: async () => {
      const data = await api.maintenance.tickets.notes.list(workOrderId);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(entityNoteListItemSchema, data);
      if (!validation.valid) {
        logger.error('Invalid work order notes query response:', validation.errors);
        throw new Error('Invalid work order notes data received from API');
      }
      return validation.data! as Note[];
    },
    enabled: !!workOrderId,
  });
};

export const useCreateWorkOrderNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (note: { workOrderId: string; content: string }) => api.maintenance.tickets.notes.create(note),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['tickets', 'notes', variables.workOrderId] });
      showToast('Note created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create note', 'error');
    }
  });
};

export const useUpdateWorkOrderNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { content: string } }) =>
      api.maintenance.tickets.notes.update(id, updates.content),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tickets', 'notes'] });
      showToast('Note updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update note', 'error');
    }
  });
};

export const useDeleteWorkOrderNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.maintenance.tickets.notes.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tickets', 'notes'] });
      showToast('Note deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete note', 'error');
    }
  });
};


